/**
 * AdHocSocialModal · iter 275 round 51.
 *
 * Modal mostrato dalla Galleria quando l'agente seleziona 1+ foto e
 * clicca "Crea contenuti social". Raccoglie 3 campi opzionali (titolo,
 * prezzo, città) + tipo annuncio (Vendita/Affitto), poi POST verso
 * `/api/social/ad-hoc-property` che crea una property nascosta. Al
 * successo si naviga in `/dashboard/social?pid={id}` e il Social Studio
 * apre direttamente quella scheda.
 *
 * Tutti i campi sono opzionali: se vuoti, il backend usa fallback
 * sensati (es. "Senza annuncio" come titolo, città dell'agenzia).
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Spinner } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdHocSocialModal({ open, onClose, photoIds = [] }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [city, setCity] = useState("");
  const [listingType, setListingType] = useState("Vendita");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim() || null,
        price: price ? Number(String(price).replace(/[^\d.]/g, "")) || null : null,
        city: city.trim() || null,
        listing_type: listingType,
        photo_ids: photoIds,
      };
      const { data } = await api.post("/social/ad-hoc-property", payload);
      toast.success(
        photoIds.length > 0
          ? `Contenuti pronti su ${data.photo_count} foto`
          : "Contenuti pronti",
      );
      onClose?.();
      navigate(`/dashboard/social?pid=${data.property_id}`);
    } catch (err) {
      toast.error(apiError(err, "Errore creazione contenuti"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="adhoc-social-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-md p-5 shadow-2xl border border-[#EAE7DF]"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-editorial text-xl text-[#1C1C1A]">
              Crea contenuto social
            </h2>
            <p className="text-[12.5px] text-[#6B6A65] mt-0.5">
              {photoIds.length > 0
                ? `${photoIds.length} foto scelte. Aggiungi qualche dettaglio (facoltativo).`
                : "Aggiungi qualche dettaglio per i tuoi contenuti."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 -mr-1.5 -mt-1.5 rounded hover:bg-[#F1F4ED]"
            aria-label="Chiudi"
            data-testid="adhoc-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <Field
            label="Titolo"
            placeholder="es. Trilocale Brera"
            value={title}
            onChange={setTitle}
            testid="adhoc-title"
          />
          <div className="grid grid-cols-2 gap-2.5">
            <Field
              label="Prezzo €"
              placeholder="480000"
              value={price}
              onChange={setPrice}
              inputMode="numeric"
              testid="adhoc-price"
            />
            <Field
              label="Città"
              placeholder="Milano"
              value={city}
              onChange={setCity}
              testid="adhoc-city"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.12em] text-[#6B6A65] mb-1">
              Tipo
            </label>
            <div className="inline-flex bg-[#F1F4ED] rounded-md p-0.5 text-[12.5px]">
              {["Vendita", "Affitto"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setListingType(k)}
                  className={`px-3 py-1 rounded font-medium transition-colors ${
                    listingType === k
                      ? "bg-white text-[#1C1C1A] shadow-sm"
                      : "text-[#6B6A65] hover:text-[#1C1C1A]"
                  }`}
                  data-testid={`adhoc-type-${k.toLowerCase()}`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-4 text-[11.5px] text-[#6B6A65] leading-relaxed">
          Tutto facoltativo: se lasci vuoto useremo "Senza annuncio" come
          titolo e i dati della tua agenzia. Il QR sui materiali punterà al
          tuo profilo agente (non a una pagina annuncio).
        </p>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] text-[#6B6A65] hover:text-[#1C1C1A] px-3 py-2"
            data-testid="adhoc-cancel"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 bg-[#1C1C1A] hover:bg-black disabled:opacity-60 text-white text-[13px] font-semibold rounded-md px-4 py-2"
            data-testid="adhoc-submit"
          >
            {submitting ? (
              <><Spinner size={13} className="animate-spin" /> Sto preparando…</>
            ) : (
              <>Crea contenuto <ArrowRight size={13} weight="bold" /></>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, inputMode, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.12em] text-[#6B6A65] mb-1">
        {label}
      </label>
      <input
        type="text"
        inputMode={inputMode || "text"}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#F8F7F2] border border-[#EAE7DF] rounded-md px-3 py-2 text-[14px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E]"
        data-testid={testid}
      />
    </div>
  );
}
